import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { type CafeteriaOrderFilters, type CreateCafeteriaRefund, cafeteriaOrdersApi } from '../orders';

const keys = {
  all: ['cafeteria', 'orders'] as const,
  list: (filters: CafeteriaOrderFilters) => ['cafeteria', 'orders', 'list', filters] as const,
  detail: (id: string) => ['cafeteria', 'orders', 'detail', id] as const,
};

export function useCafeteriaOrdersQuery(filters: CafeteriaOrderFilters) {
  return useQuery({
    queryKey: keys.list(filters),
    queryFn: () => cafeteriaOrdersApi.list(filters),
    placeholderData: keepPreviousData,
  });
}

export function useCafeteriaOrderQuery(id: string | null) {
  return useQuery({
    queryKey: keys.detail(id ?? ''),
    queryFn: () => cafeteriaOrdersApi.get(id as string),
    enabled: Boolean(id),
  });
}

// A refund changes the sale, its wallet, the wallets overview and the reports, so all of them refetch.
export function useRefundCafeteriaOrderMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ orderId, ...input }: CreateCafeteriaRefund & { orderId: string }) =>
      cafeteriaOrdersApi.refund(orderId, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.all });
      qc.invalidateQueries({ queryKey: ['cafeteria', 'wallet'] });
      qc.invalidateQueries({ queryKey: ['cafeteria', 'wallets-overview'] });
      qc.invalidateQueries({ queryKey: ['cafeteria', 'reports'] });
    },
  });
}
